import React, { createContext, useContext, useId, useMemo } from 'react'

import { InformationCircleIcon } from '@hugeicons/react'
import _ from 'lodash'
import { useFormContext } from 'react-hook-form'

import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'

import ErrorMessage from './ErrorMessage'

const Control = ({ name, control: ControlComponent, ...rest }) => (
  <ControlComponent name={name} {...rest} />
)

const HelpText = ({ id, text }) => (
  <p id={id} className="text-muted-foreground block w-full text-xs mt-1">
    {text}
  </p>
)

const FormLabel = ({
  id,
  htmlFor,
  label,
  labelInfo,
  labelClassName = '',
  vertical,
  required
}) => (
  <Label
    id={id}
    htmlFor={htmlFor}
    className={cn(
      'block text-sm font-medium text-gray-700',
      !vertical && 'lg:col-span-4 xl:col-span-3',
      labelClassName
    )}
  >
    <span className={required ? 'after:content-["*"] after:ml-0.5 after:text-danger-text' : ''}>
      {label}
    </span>
    {labelInfo && (
      <InformationCircleIcon
        variant="duotone"
        size={16}
        className="inline ml-1.5 text-gray-700 hover:text-brand cursor-help"
        title={labelInfo}
      />
    )}
  </Label>
)

/**
 * Identidade do campo.
 *
 * O `id` é gerado aqui e não exigido do consumidor: exigi-lo quebraria todos os
 * formulários existentes e transferiria para a tela uma responsabilidade que é
 * do primitivo. O prefixo do `useId` garante unicidade quando o mesmo `name`
 * aparece duas vezes na página — formulário em modal sobre formulário em tela.
 *
 * Devolve também os ids de ajuda e erro, porque `aria-describedby` só liga o
 * campo à mensagem se ambos os lados conhecerem o mesmo identificador.
 */
function useFieldIds(name, providedId) {
  const generated = useId()
  // `name` é opcional no FormGroup — costuma viver no controle aninhado.
  const base = providedId ?? `${generated}${name ?? 'field'}`
  return {
    controlId: base,
    labelId: `${base}-label`,
    helpId: `${base}-help`,
    errorId: `${base}-error`
  }
}

/**
 * Leva a identidade do campo ao controle mesmo quando ele não vem pela prop
 * `control`.
 *
 * O padrão dominante neste app aninha o controle como `children`, tipicamente
 * dentro de um `InputGroup`:
 *
 *     <FormGroup label="Nome" vertical>
 *       <InputGroup><FormControl name="name" /></InputGroup>
 *     </FormGroup>
 *
 * Sem o contexto, o `htmlFor` do rótulo apontaria para um id que nenhum
 * elemento carrega — referência pendurada, que é pior que não ter associação
 * nenhuma, porque promete o que não entrega.
 */
export const FormFieldContext = createContext(null)

export const useFormField = () => useContext(FormFieldContext)

const FormGroup = ({
  name,
  id: providedId = undefined,
  onChange = null,
  label,
  labelInfo = null,
  labelClassName = '',
  wrapperClassName = '',
  horizontal = true,
  vertical = false,
  margin = 6,
  required = undefined,
  helpText = null,
  control = undefined,
  solid = false,
  children = undefined,
  ...rest
}) => {
  const { controlId, labelId, helpId, errorId } = useFieldIds(name, providedId)

  // `aria-describedby` referencia só o que existe no DOM neste render. O
  // ErrorMessage não renderiza nada quando o campo é válido, então incluir o
  // id do erro incondicionalmente produziria a mesma referência pendurada que
  // deixa o diálogo sem nome acessível — o defeito que esta change corrige.
  const formContext = useFormContext()
  const hasError = !!_.get(formContext?.formState?.errors, name)
  const describedBy =
    [helpText ? helpId : null, hasError ? errorId : null].filter(Boolean).join(' ') ||
    undefined

  const controlProps = {
    id: controlId,
    name,
    control,
    onChange,
    required,
    solid,
    'aria-describedby': describedBy,
    ...rest
  }

  // `labelId` acompanha porque o controle referencia o rótulo por
  // `aria-labelledby`: o rótulo sempre existe, então essa referência nunca
  // fica pendurada — diferente do `htmlFor`, que depende de alguém reivindicar
  // o id. Os dois coexistem: `htmlFor` dá o clique-para-focar, o
  // `aria-labelledby` garante o nome acessível.
  const fieldContext = useMemo(
    () => ({ controlId, labelId, describedBy, name }),
    [controlId, labelId, describedBy, name]
  )

  const body = (
    <FormFieldContext.Provider value={fieldContext}>
      {control && <Control {...controlProps} />}
      {children}
      {helpText && <HelpText id={helpId} text={helpText} />}
      <ErrorMessage id={errorId} name={name} />
    </FormFieldContext.Provider>
  )

  if (vertical)
    return (
      <div className={`mb-${margin} ${wrapperClassName}`}>
        {label && (
          <FormLabel
            id={labelId}
            htmlFor={controlId}
            label={label}
            labelInfo={labelInfo}
            labelClassName={labelClassName}
            horizontal={horizontal}
            vertical={vertical}
            required={required}
          />
        )}
        {body}
      </div>
    )

  return (
    <div className={`grid grid-cols-1 lg:grid-cols-12 gap-2 mb-${margin} items-start ${wrapperClassName}`}>
      <div className="lg:col-span-4 xl:col-span-3">
        <FormLabel
          id={labelId}
          htmlFor={controlId}
          label={label}
          labelInfo={labelInfo}
          labelClassName={labelClassName}
          horizontal={horizontal}
          vertical={vertical}
          required={required}
        />
      </div>
      <div className="lg:col-span-8 xl:col-span-9">{body}</div>
    </div>
  )
}

export { FormGroup }
export default FormGroup
